import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

// Floating geometric shapes that move with scroll
export const FloatingShapes = ({ scrollYProgress }) => {
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -400]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const rotate1 = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const rotate2 = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const scale1 = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.5, 0.8]);
  const scale2 = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.2, 1]);

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const y1Spring = useSpring(y1, springConfig);
  const y2Spring = useSpring(y2, springConfig);
  const rotate1Spring = useSpring(rotate1, springConfig);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Large circle - top left */}
      <motion.div
        style={{ y: y1Spring, rotate: rotate1Spring, scale: scale1 }}
        className="absolute -top-20 -left-20 w-96 h-96 rounded-full border-2 border-sky-200/30"
      />
      
      {/* Water drop shape - top right */}
      <motion.div
        style={{ y: y2Spring, rotate: rotate2 }}
        className="absolute top-40 -right-10 w-64 h-64"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path
            d="M50 5 C50 5 90 50 90 70 C90 90 70 95 50 95 C30 95 10 90 10 70 C10 50 50 5 50 5"
            fill="none"
            stroke="rgba(14, 165, 233, 0.15)"
            strokeWidth="1"
          />
        </svg>
      </motion.div>

      {/* Small circles scattered */}
      <motion.div
        style={{ y: y3, scale: scale2 }}
        className="absolute top-1/3 left-1/4 w-32 h-32 rounded-full bg-gradient-to-br from-sky-100/20 to-transparent"
      />
      
      <motion.div
        style={{ y: y2, rotate: rotate1 }}
        className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full border border-cyan-200/20"
      />

      {/* Wave lines */}
      <motion.div
        style={{ y: y1 }}
        className="absolute top-1/2 left-0 right-0"
      >
        <svg viewBox="0 0 1440 100" className="w-full h-20 opacity-10">
          <path
            d="M0,50 C360,100 720,0 1080,50 C1260,75 1350,25 1440,50"
            fill="none"
            stroke="#0ea5e9"
            strokeWidth="2"
          />
        </svg>
      </motion.div>
    </div>
  );
};

// Parallax section wrapper with scroll-linked animations
export const ParallaxSection = ({ children, className = '', speed = 0.5, id }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100 * speed, -100 * speed]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.95, 1, 1, 0.95]);

  return (
    <motion.section
      ref={ref}
      id={id}
      style={{ y, opacity, scale }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Scroll-triggered reveal with continuous animation
export const ScrollReveal = ({ 
  children, 
  direction = 'up', 
  delay = 0,
  className = '' 
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const directions = {
    up: { initial: 80, final: -20 },
    down: { initial: -80, final: 20 },
    left: { initial: 80, final: -20 },
    right: { initial: -80, final: 20 },
  };

  const isHorizontal = direction === 'left' || direction === 'right';
  const movement = directions[direction];

  const transform = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [movement.initial, 0, 0, movement.final]
  );
  
  const opacity = useTransform(
    scrollYProgress, 
    [0, 0.2, 0.8, 1], 
    [0, 1, 1, 0]
  );

  const springConfig = { stiffness: 100, damping: 30 };
  const transformSpring = useSpring(transform, springConfig);

  return (
    <motion.div
      ref={ref}
      style={{
        [isHorizontal ? 'x' : 'y']: transformSpring,
        opacity,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Rotating element on scroll
export const ScrollRotate = ({ children, className = '', direction = 1 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360 * direction]);
  const rotateSpring = useSpring(rotate, { stiffness: 50, damping: 20 });

  return (
    <motion.div
      ref={ref}
      style={{ rotate: rotateSpring }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Scale on scroll
export const ScrollScale = ({ children, className = '', minScale = 0.8, maxScale = 1.2 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [minScale, maxScale, minScale]);
  const scaleSpring = useSpring(scale, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      ref={ref}
      style={{ scale: scaleSpring }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Horizontal scroll-linked movement
export const ScrollHorizontal = ({ children, className = '', distance = 200 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const x = useTransform(scrollYProgress, [0, 1], [-distance, distance]);
  const xSpring = useSpring(x, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      ref={ref}
      style={{ x: xSpring }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// 3D tilt effect on scroll
export const ScrollTilt = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [15, 0, -15]);
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-10, 0, 10]);

  return (
    <motion.div
      ref={ref}
      style={{ 
        rotateX, 
        rotateY,
        transformPerspective: 1000,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Stagger children animation on scroll
export const ScrollStagger = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });

  return (
    <motion.div ref={ref} className={className}>
      {children}
    </motion.div>
  );
};

// Water ripple effect component
export const WaterRipple = ({ className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const scale1 = useTransform(scrollYProgress, [0, 0.5, 1], [0.5, 1.5, 0.5]);
  const scale2 = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1.2, 0.3]);
  const scale3 = useTransform(scrollYProgress, [0, 0.5, 1], [0.1, 1, 0.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 0.6, 0.6, 0]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <motion.div
        style={{ scale: scale1, opacity }}
        className="absolute inset-0 rounded-full border-2 border-sky-400/30"
      />
      <motion.div
        style={{ scale: scale2, opacity }}
        className="absolute inset-0 rounded-full border-2 border-sky-400/20"
      />
      <motion.div
        style={{ scale: scale3, opacity }}
        className="absolute inset-0 rounded-full border-2 border-sky-400/10"
      />
    </div>
  );
};

// Morphing blob shape
export const MorphingBlob = ({ className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const borderRadius = useTransform(
    scrollYProgress,
    [0, 0.25, 0.5, 0.75, 1],
    [
      '60% 40% 30% 70% / 60% 30% 70% 40%',
      '30% 60% 70% 40% / 50% 60% 30% 60%',
      '60% 40% 30% 70% / 60% 30% 70% 40%',
      '40% 60% 60% 40% / 70% 30% 70% 30%',
      '60% 40% 30% 70% / 60% 30% 70% 40%',
    ]
  );

  const rotate = useTransform(scrollYProgress, [0, 1], [0, 180]);

  return (
    <motion.div
      ref={ref}
      style={{ borderRadius, rotate }}
      className={`bg-gradient-to-br from-sky-200/30 to-cyan-200/30 ${className}`}
    />
  );
};

export default {
  FloatingShapes,
  ParallaxSection,
  ScrollReveal,
  ScrollRotate,
  ScrollScale,
  ScrollHorizontal,
  ScrollTilt,
  ScrollStagger,
  WaterRipple,
  MorphingBlob,
};
